import * as React from "react";
import ReactDOMServer from "react-dom/server";
import { Portfolio, RankDir } from "context/reports/comfort/types";
import { orderPortfolio } from "../../../context/reports/comfort/utils";
import { navigate } from "../../../utils/routes/navigate";
import { ToolTip } from "./components/ToolTip";
import { createLegendItemsUsingDates } from "./utils";
import { zoneChartClickEvent } from "../../../utils/eventTracking";
import { TRACKING_EVENT_CHART_CLICK } from "../../../context/reports/workflow/constants";
import { scrollBar2DChartBaseConfig } from "../ChartBaseConfigs";

interface IndexByString {
  [key: string]: any;
}
interface ChartDataObject {
  categories: {
    category: IndexByString[];
  };
  dataset: {
    current: {
      from: string;
      to: string;
      comfortable: IndexByString[];
      hot: IndexByString[];
      cold: IndexByString[];
    };
    compare: {
      from: string;
      to: string;
      comfortable: IndexByString[];
      hot: IndexByString[];
      cold: IndexByString[];
    };
  };
}

const CHART_SETTINGS = {
  showValues: true,
  numberSuffix: "%",
  adjustDiv: false,
  canvasBgColor: "#F2FDF3",
  canvasBgAlpha: 100,
  plotFillHoverColor: "#207327", // palette.green9,
  yAxisName:
    "Building comfort during occupancy (zones within 2℃ of set point, averaged across all zones)",
  stack100Percent: false,
  reverseLegend: true,
  theme: "fusion",
  ...scrollBar2DChartBaseConfig,
};

const CHART_CONFIG = {
  type: "scrollbar2d",
  width: "100%",
  height: "500",
};

export function getPortfolioChartConfig(
  rawData: Portfolio,
  rankDir: RankDir,
  chartTitle: string,
  current: { from: string; to: string },
  compare?: { from: string; to: string }
) {
  const sitesDataArray = orderPortfolio(rankDir, rawData);

  const formattedData = sitesDataArray.reduce(
    (acc: ChartDataObject, data) => {
      const siteId = data[0];
      const d = data[1];

      // Exit early if site has no comfort data
      if (d.series.current.length < 1) return acc;

      if (acc.dataset.current.from === "" && acc.dataset.current.to === "") {
        acc.dataset.current.from = current.from;
        acc.dataset.current.to = current.to;
      }

      acc.categories.category.push({
        siteId,
        label: d.name,
      });

      const pctComfort = Math.round(d.summary.current.pctComfort * 100) / 100;
      acc.dataset.current.comfortable.push({
        value: pctComfort,
        displayValue: `${Math.round(pctComfort)}%`,
        tooltext: ReactDOMServer.renderToStaticMarkup(
          <ToolTip
            name={d.name}
            category="comfortable"
            categoryOccupiedPct={pctComfort}
            from={d.summary.current.from}
            to={d.summary.current.to}
          />
        ),
      });

      if (d.summary.compare && d.summary.compare.from !== "") {
        const { from, to, pctComfort } = d.summary.compare;

        if (
          acc.dataset.compare.from === "" &&
          acc.dataset.compare.to === "" &&
          compare
        ) {
          acc.dataset.compare.from = compare.from;
          acc.dataset.compare.to = compare.to;
        }

        acc.dataset.compare.comfortable.push({
          value: pctComfort,
          displayValue: `${Math.round(pctComfort)}%`,
          tooltext: ReactDOMServer.renderToStaticMarkup(
            <ToolTip
              name={d.name}
              category="comfortable"
              categoryOccupiedPct={pctComfort}
              from={from}
              to={to}
            />
          ),
        });
      }

      return acc;
    },
    {
      categories: {
        category: [],
      },
      dataset: {
        current: {
          from: "",
          to: "",
          comfortable: [],
          cold: [],
          hot: [],
        },
        compare: {
          from: "",
          to: "",
          comfortable: [],
          cold: [],
          hot: [],
        },
      },
    }
  );

  const currentDataSet =
    formattedData.dataset.current.from !== ""
      ? {
          seriesname: createLegendItemsUsingDates(
            formattedData.dataset.current.from,
            formattedData.dataset.current.to
          ),
          data: formattedData.dataset.current.comfortable,
          color: "#3F9142", // palette.green7
          visible: true,
        }
      : undefined;

  const compareDataSet =
    formattedData.dataset.compare.from !== ""
      ? {
          seriesname: createLegendItemsUsingDates(
            formattedData.dataset.compare.from,
            formattedData.dataset.compare.to
          ),
          data: formattedData.dataset.compare.comfortable,
          color: "#7dc580", // palette.green5
        }
      : undefined;

  const dataSource = {
    chart: {
      ...CHART_SETTINGS,
      yAxisMaxValue: 100,
    },
    categories: [
      {
        category: formattedData.categories.category,
      },
    ],
    dataset: [currentDataSet, compareDataSet],
  };

  return {
    ...CHART_CONFIG,
    dataSource,
    events: {
      dataplotClick: function handleClick(_e: any, d: any) {
        const bars = formattedData.dataset.current.comfortable;

        zoneChartClickEvent(
          {
            chartTitle,
            d,
            bars,
            sortDir: rankDir,
          },
          TRACKING_EVENT_CHART_CLICK
        );
        const clickedBarIndex = d.index;
        const id = formattedData.categories.category[clickedBarIndex].siteId;
        const path = `/reports/performance/comfort/${id}`;
        navigate(path)();
      },
    },
  };
}
